import { LayerRenderer } from '../models/layer-renderer.model';
import { Document, FieldDataType } from 'src/app/modules/core/store/models/document.model';
import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { GoogleMapFeatures, LayerMapFeatures } from 'src/app/modules/cop/drawing/models/layer-map-features.model';
import { LayerColorsService } from 'src/app/modules/cop/drawing/layer-colors.service';
import { MapDrawingContext } from '../models/map-drawing-context.model';
import { TranslateService } from '@ngx-translate/core';
import { Feature } from '../features/feature.model';
import { AlertTropicalCycloneFeature } from '../features/alert-tropical-cyclone/alert-tropical-cyclone.feature';

export class TropicalCyclonesLayerRenderer implements LayerRenderer {
  constructor(private translateService: TranslateService) {}

  addDocuments(drawingContext: MapDrawingContext, layer: Layer, documents: Document[]): void {
    // init the layer feature set if needed
    if (!!!drawingContext.mapFeatures[layer.uId]) {
      drawingContext.mapFeatures[layer.uId] = {};
    }
    const layerFeatures = drawingContext.mapFeatures[layer.uId];

    drawingContext.clusterService.clearChanges(layer.uId);

    for (let d of documents) {
      this.renderDocument(drawingContext, layer, d, layerFeatures, this.getLabelValues(drawingContext));
    }

    drawingContext.clusterService.applyChanges(layer.uId);

    drawingContext.onLoadingDone(layer);
  }

  updateDocuments(drawingContext: MapDrawingContext, layer: Layer, documents: Document[]): void {
    // init the layer feature set if needed
    if (!!!drawingContext.mapFeatures[layer.uId]) {
      drawingContext.mapFeatures[layer.uId] = {};
    }
    const layerFeatures = drawingContext.mapFeatures[layer.uId];

    drawingContext.clusterService.clearChanges(layer.uId);

    for (let d of documents) {
      this.renderDocument(drawingContext, layer, d, layerFeatures, this.getLabelValues(drawingContext));
    }

    drawingContext.clusterService.applyChanges(layer.uId);

    drawingContext.onLoadingDone(layer);
  }

  removeDocuments(drawingContext: MapDrawingContext, layer: Layer, documentUIds?: string[]): void {
    // only proceed if the layer was rendered
    if (!!!drawingContext.mapFeatures[layer.uId]) {
      return;
    }
    const layerFeatures = drawingContext.mapFeatures[layer.uId];

    const docUIds = documentUIds ?? Object.keys(layerFeatures);

    drawingContext.clusterService.clearChanges(layer.uId);

    for (let uid of docUIds) {
      this.removeDocument(drawingContext, layer, uid, layerFeatures);
    }

    drawingContext.clusterService.applyChanges(layer.uId);
  }

  removeDocument(drawingContext: MapDrawingContext, layer: Layer, uid: string, layerFeatures: LayerMapFeatures): void {
    const docFeatures = layerFeatures[uid];
    if (!!!docFeatures) {
      return;
    }

    docFeatures?.features.forEach(feature => {
      feature.destroy();
    });

    delete layerFeatures[uid];
  }

  renderDocument(
    drawingContext: MapDrawingContext,
    layer: Layer,
    document: Document,
    layerFeatures: LayerMapFeatures,
    labelValues: { [key: string]: string }
  ): void {
    // init the document feature set if needed
    if (!!!layerFeatures[document.uid]) {
      layerFeatures[document.uid] = {
        markers: [],
        polygons: [],
        polylines: [],
        circles: [],
        rectangles: [],
        imageOverlays: [],
        features: [],
        hash: null
      };
    }

    // get a reference to the feature that is associated with the
    // current document. Create it, if it does not already exist.
    const docFeatures = layerFeatures[document.uid];
    let feature: Feature = null;
    if (docFeatures.features?.length > 0) {
      feature = docFeatures.features[0];
    } else {
      feature = new AlertTropicalCycloneFeature(labelValues);
      docFeatures.features.push(feature);
    }

    const options = {
      layer,
      document
    };

    feature.render(drawingContext, options);
  }

  private getLabelValues(drawingContext: MapDrawingContext): { [key: string]: string } {
    const labelValues: { [key: string]: string } = {};

    labelValues['MAP.CYCLONES.CAT'] = this.translateService.instant('MAP.CYCLONES.CAT');
    labelValues['MAP.CYCLONES.WINDS'] = this.translateService.instant('MAP.CYCLONES.WINDS');
    labelValues['MAP.CYCLONES.GUSTS'] = this.translateService.instant('MAP.CYCLONES.GUSTS');
    labelValues['MAP.CYCLONES.MOVING'] = this.translateService.instant('MAP.CYCLONES.MOVING');

    labelValues['cultureCode'] = drawingContext.userProfile.culture?.cultureCode;
    labelValues['distanceUnit'] = drawingContext.userProfile.culture?.distanceUnit;
    return labelValues;
  }
}
